/**
 * BoardSnapshotView -- /board (P17.3 / #458).
 *
 * One-page executive board snapshot. Composes the same shape the
 * PDF renderer uses (`BoardSnapshot`) into a print-friendly HTML
 * page. Layout matches the PDF top-to-bottom for easy comparison:
 *   * Header strip
 *   * Headline KPIs row (overall RAG chip + 4 metric tiles)
 *   * Outcome RAG quadrant (4 cells with per-cell RAG tone)
 *   * Hot spots table (top 5)
 *   * MVB + Framework progress (two columns)
 *   * Resilience trend (reuses the existing ResilienceTrendCard
 *     for the 30-day chart; lives in twin/home/)
 *
 * Auth: admin OR approver. Backend returns 403 for other
 * positions; the view shows the error inline.
 *
 * Module-boundary rule: lives in shared/board because the board
 * is a cross-module aggregator (twin + framework + foundation/
 * mvb). It reads from shared/api/* and renders. The cascade
 * impact card is a sister composition leaf at twin/cascade/.
 */
import { said } from "shared/i18n/said";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { FindingLink } from "shared/components/FindingLink";
import { useToast } from "shared/toast/ToastProvider";
import { formatDateTime } from "shared/time/formatDate";
import { i18n } from "shared/i18n";

import { MarsadIcon } from "shared/components/MarsadIcon";
import type { ApiError } from "shared/api/client";
import type {
  BoardHotSpot,
  BoardOutcomeRag,
  BoardSnapshot,
  Rag,
} from "shared/api/types";
import { FullscreenToggle, useFullscreen } from "shared/layout/FullscreenToggle";
import { formatSAR, MONEY_NOT_MEASURED } from "shared/money/money";

import { ragWordKey } from "shared/board/ragTone";
import { useBoardSnapshot } from "./useBoardSnapshot";
import { AsOfBanner, isAfterAsOf } from "shared/components/AsOfBanner";
import { AsOfPicker } from "shared/components/AsOfPicker";
import { entityDetailPath } from "shared/routes/entityRoutes";
import { useAsOf } from "shared/hooks/useAsOf";
import { useDownloadBoardPdf } from "./useDownloadBoardPdf";
import { canDownloadBoardPdf } from "shared/api/authority";
import { useMe } from "shared/api/useMe";

// The formatSAR sentinel is for the UI to translate, never to render raw
// (the P254-era demo screenshots caught "__not_measured__" leaking into the
// hot-spots table). Same convention as WarRoomView's sar() helper.
function sarOrDash(raw: string | null | undefined): string {
  const out = formatSAR(raw);
  return out === MONEY_NOT_MEASURED ? "--" : out;
}

const RAG_TONE: Record<Rag, string> = {
  red: "bg-red-100 text-red-900 border-red-200",
  amber: "bg-amber-100 text-amber-900 border-amber-200",
  green: "bg-emerald-100 text-emerald-900 border-emerald-200",
  /* Round 12 B1 -- slate, deliberately. An estate nobody has looked at
     is not a paler shade of fine and not a warning either; it is the
     absence of a verdict, and the one colour it must not borrow is the
     one beside it. */
  not_assessed: "bg-slate-100 text-slate-700 border-slate-300",
};

const OUTCOME_LABELS: Record<string, string> = {
  minimize_customer_harm: "Customer harm",
  retain_customer_confidence: "Customer confidence",
  regulatory_engagement: "Regulatory",
  protect_safety_and_soundness: "Safety + soundness",
};

export function BoardSnapshotView() {
  const { t } = useTranslation();
  // D3 -- the board stamped an as-of and could not be moved to one.
  // An executive asked "what did this look like at the last board
  // meeting?" had no way to ask it.
  const asOf = useAsOf();
  const toast = useToast();
  const query = useBoardSnapshot(asOf ? { as_of: asOf } : {});
  // D4 -- "is this better or worse than last time?" is the first
  // question in the room, and the board had no way to ask it. The
  // comparison is a SECOND read of the same composer at an earlier
  // time, so the two figures are built the same way.
  const [compareWith, setCompareWith] = useState<string>("");
  const comparison = useBoardSnapshot(
    compareWith ? { as_of: `${compareWith}T00:00:00Z` } : {},
  );
  const prior = compareWith ? comparison.data : undefined;
  const download = useDownloadBoardPdf();
  const me = useMe();
  // Undefined while /me loads: offer the button, as before, rather than
  // flashing the note at an approver.
  const canIssuePdf = !me.data || canDownloadBoardPdf(me.data);
  const rootRef = useRef<HTMLElement | null>(null);
  const { isFullscreen } = useFullscreen(rootRef);

  if (query.isLoading) {
    return (
      <p data-testid="board-loading" className="text-sm text-slate-500">
        {t("modules.board.loading")}
      </p>
    );
  }

  if (query.isError) {
    return <BoardError error={query.error} />;
  }

  const snapshot = query.data;
  if (!snapshot) return null;

  return (
    <article
      ref={rootRef}
      className={isFullscreen ? "min-h-screen w-full bg-white p-6" : "max-w-6xl"}
      data-testid="board-snapshot-view"
    >
      <header className="flex flex-wrap items-baseline justify-between gap-3 mb-4">
        <div>
          <h1 className="font-display text-2xl font-semibold">
            <MarsadIcon
              name="dossier"
              size={20}
              className="me-2 inline-block align-[-0.1em] text-brand-brassDark"
            />
            {t("modules.board.heading")}
          </h1>
          <p className="text-xs text-slate-500 tabular-nums mt-0.5">
            <span data-testid="board-as-of">
              {t("modules.board.asOf", {
                value: formatDateTime(snapshot.as_of, i18n.language),
              })}
            </span>{" "}
            {/* Round 18 Q4 -- the board's terms are the glossary's, and
                the glossary was typed by hand for three rounds. */}
            <Link
              to="/glossary"
              data-testid="board-glossary-link"
              className="ms-2 text-brand-brassDark hover:underline"
            >
              {t("modules.board.glossary")}
            </Link>
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Round 14 B4 -- the board now reads for the reviewer and the
              Head of Operational Resilience; the signed PDF is still the
              approver's to issue, and a button that 403s is not offered. */}
          {!canIssuePdf && (
            <span data-testid="board-pdf-not-yours" className="text-xs text-slate-500">
              {t("modules.board.pdfIsApprovers")}
            </span>
          )}
          {canIssuePdf && (
            <button
              type="button"
              onClick={() =>
                // C4 -- the PDF downloaded in silence, so on a browser
                // that saves without prompting the button looked dead
                // and was pressed again.
                download.mutate(
                  {},
                  {
                    onSuccess: () =>
                      toast.push({
                        tone: "success",
                        message: t("modules.board.downloadDone"),
                      }),
                    onError: (e) =>
                      toast.push({
                        tone: "error",
                        message: t("modules.board.downloadFailed"),
                        detail: e.message,
                      }),
                  },
                )
              }
              disabled={download.isPending}
              data-testid="board-download-pdf"
              className="px-3 py-1.5 rounded bg-brand-brass text-brand-navy text-sm font-medium hover:bg-brand-brassDark disabled:opacity-50"
            >
              {download.isPending
                ? t("modules.board.downloadInProgress")
                : t("modules.board.downloadButton")}
            </button>
          )}
          <FullscreenToggle targetRef={rootRef} />
        </div>
      </header>

      <AsOfPicker />
      <AsOfBanner asOf={asOf} />
      {/* D1 -- rewinding the board left every figure unchanged, so the
          control read as broken. It is not: only the SEALED resilience
          counts replay at an instant, because they read the chain.
          Everything finding-driven is current state by the P2.6 lock,
          and a comparison built on it would report no change ever.
          The board says which is which rather than leaving a reader to
          conclude the rewind did nothing. */}
      {asOf && (
        <p data-testid="board-as-of-scope" className="mb-3 text-xs text-slate-600">
          {t("modules.board.asOfScope")}
        </p>
      )}
      <BoardComparison
        value={compareWith}
        onChange={setCompareWith}
        current={query.data}
        prior={prior}
        loading={Boolean(compareWith) && comparison.isLoading}
      />

      <HeadlineStrip snapshot={snapshot} />
      <OutcomeRagQuadrant outcomes={snapshot.outcome_rag} />
      <HotSpotsTable
        asOf={asOf}
        hotSpots={snapshot.hot_spots}
        notAssessed={snapshot.headline.overall_rag === "not_assessed"}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <MvbBlock mvb={snapshot.mvb} />
        <FrameworkBlock fa={snapshot.framework_assessments} />
      </div>
    </article>
  );
}

// ── Sub-sections ───────────────────────────────────────────────────

function BoardError({ error }: { error: ApiError }) {
  const { t } = useTranslation();
  const forbidden = error.status === 403;
  return (
    <article className="max-w-3xl">
      <h1 className="font-display text-2xl font-semibold mb-2">
        {t("modules.board.heading")}
      </h1>
      <p
        data-testid={forbidden ? "board-forbidden" : "board-error"}
        className={`text-sm rounded p-4 ${
          forbidden
            ? "bg-amber-50 border border-amber-200 text-amber-900"
            : "bg-red-50 border border-red-200 text-red-900"
        }`}
      >
        {forbidden ? t("modules.board.forbidden") : error.detail}
      </p>
    </article>
  );
}

function HeadlineStrip({ snapshot }: { snapshot: BoardSnapshot }) {
  const { t } = useTranslation();
  const h = snapshot.headline;
  // P26 (#545): MVB intersection chip on the headline -- mirrors
  // the P20.2 CascadeBanner pattern. Hidden when the bank has no
  // MVB-flagged IBSes (mvb_total_count = 0); showing "0 of 0
  // impacted" would confuse the operator before any MVB has been
  // declared.
  const showMvb = h.mvb_total_count > 0;
  // The outcome that set the overall RAG, and only when it has a
  // finding to open. RAG order is worst-first by construction here.
  const RAG_RANK: Record<string, number> = { red: 0, amber: 1, green: 2 };
  const worstFinding = [...snapshot.outcome_rag]
    .sort((a, b) => (RAG_RANK[a.rag] ?? 9) - (RAG_RANK[b.rag] ?? 9))
    .find((o) => o.rag === h.overall_rag && o.headline_finding_id);
  const mvbPct = Math.round((h.mvb_impacted_share ?? 0) * 100);
  return (
    <section data-testid="board-headline-strip" className="mb-4">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
        {/* C4 -- the chip was inert, and RED is the number a board
            member asks about first. The overall RAG is the worst of
            the four outcomes, so its evidence is that outcome's worst
            finding -- the same destination the quadrant cell offers,
            reached from the figure that raised the question. No
            finding recorded means nothing to open, and it stays a
            chip rather than a link to somewhere approximate. */}
        {/*
            C4, C3, and now E7 -- the third pass on the same chip, and
            the one that makes it match its neighbours.

            C4 gave it a destination and C3 gave it a fallback, but
            both hung a 10px link UNDER the word while the four tiles
            beside it are anchors end to end. So the biggest number on
            a board pack, the one a board member reaches for first, was
            the only one where pressing it did nothing -- and the small
            print beneath it is not where a hand goes.

            The whole chip is the link now, as the tiles are. Where the
            worst outcome records a finding it opens that finding;
            where it does not, it opens the rows the figure counts,
            because inert is the one thing this chip must not be.
        */}
        <RagChip
          rag={h.overall_rag}
          weighted={h.overall_rag_revenue_weighted}
          to={
            worstFinding
              ? `/twin/findings/${worstFinding.headline_finding_id}`
              : "/twin/cascade?severity=critical"
          }
          openLabel={
            worstFinding
              ? t("modules.board.headline.openWorst")
              : t("modules.board.headline.openRows")
          }
          weightedLabel={
            h.overall_rag_revenue_weighted !== h.overall_rag
              ? t("modules.board.headline.weightedSubline", {
                  weighted: h.overall_rag_revenue_weighted.toUpperCase(),
                })
              : null
          }
        />
        {/* All four are counted BY the cascade, which is the page that
            enumerates them -- so that is where each one opens. */}
        <KpiTile
          testid="board-kpi-ibs-impacted"
          label={t("modules.board.kpi.ibsImpacted")}
          value={`${h.ibs_impacted} / ${h.ibs_count}`}
          /* D2 -- filtered to what this tile counts. Unfiltered it
             opened a page counting 13 beside a tile saying 10, which
             reads as a contradiction and is two honest answers to two
             questions. */
          to="/twin/cascade?kind=ibs_service"
        />
        <KpiTile
          testid="board-kpi-products"
          label={t("modules.board.kpi.productsAtRisk")}
          value={String(h.products_at_risk_total)}
          to="/twin/cascade"
        />
        <KpiTile
          testid="board-kpi-revenue-annual"
          label={t("modules.board.kpi.revenueAnnual")}
          value={sarOrDash(h.revenue_at_risk_annual)}
          to="/twin/cascade"
        />
        <KpiTile
          testid="board-kpi-revenue-daily"
          label={t("modules.board.kpi.revenueDaily")}
          value={sarOrDash(h.revenue_at_risk_daily)}
          to="/twin/cascade"
        />
      </div>
      {((h.cif_count ?? 0) > 0 || (h.cif_under_assessment ?? 0) > 0) && (
        <p data-testid="board-headline-cif" className="mt-2 text-xs text-slate-600">
          {/* P429.6 -- counted beside the IBS figure above, never in it.
              Round 20 P19 -- each population of functions named. */}
          {[
            (h.cif_count ?? 0) > 0
              ? t("modules.board.headline.cifAdmitted", {
                  total: h.cif_count,
                  impacted: h.cif_impacted ?? 0,
                }) +
                ((snapshot.tolerance_headroom?.cif_assessed ?? 0) > 0
                  ? ", " +
                    t("modules.board.headline.cifAdmittedBreached", {
                      breached: snapshot.tolerance_headroom?.cif_breached ?? 0,
                    })
                  : "")
              : null,
            (h.cif_under_assessment ?? 0) > 0
              ? t("modules.board.headline.cifUnderAssessment", {
                  count: h.cif_under_assessment ?? 0,
                  breach: h.cif_under_assessment_breach_findings ?? 0,
                })
              : null,
          ]
            .filter(Boolean)
            .join("; ")}
        </p>
      )}
      {showMvb && (
        <div className="mt-2 flex flex-wrap gap-x-2 gap-y-1">
          <span
            data-testid="board-headline-mvb"
            data-mvb-impacted={h.mvb_impacted_count}
            data-mvb-total={h.mvb_total_count}
            data-mvb-pct={mvbPct}
            className="rounded bg-indigo-50 ring-1 ring-indigo-200 text-indigo-800 px-2 py-0.5 text-xs font-medium tabular-nums"
          >
            {t("modules.board.headline.mvbIntersection", {
              impacted: h.mvb_impacted_count,
              total: h.mvb_total_count,
              pct: mvbPct,
            })}
            {(snapshot.tolerance_headroom?.assessed ?? 0) > 0 &&
              ", " +
                t("modules.board.headline.mvbBreached", {
                  breached: snapshot.tolerance_headroom?.mvb_breached ?? 0,
                })}
            {snapshot.mvb.declaration_id &&
              snapshot.mvb.ibs_count_scoped_admitted != null &&
              "; " +
                t("modules.board.headline.mvbFrozenSet", {
                  scoped: snapshot.mvb.ibs_count_scoped,
                  admitted: snapshot.mvb.ibs_count_scoped_admitted,
                })}
          </span>
        </div>
      )}

      {/* D4 -- why it is that colour. A RED chip beside "0 of 12
          tolerances breached" reads as a contradiction unless the page
          says the two are measuring different things. */}
      {h.overall_rag_because && (
        <p data-testid="board-rag-because" className="mt-2 text-xs text-slate-600">
          {/* Round 9 G -- composed in the reader's language. The
              severity arrives as a VALUE and the count chooses the
              plural; `rag` is upper-cased here rather than in the
              catalogue, because which case a word takes is a property
              of the language. Falls back to the composed English so a
              key that has not landed reads as English. */}
          {h.overall_rag_because_key
            ? String(
                t(h.overall_rag_because_key, {
                  ...(h.overall_rag_because_params ?? {}),
                  rag: String(h.overall_rag_because_params?.rag ?? "").toUpperCase(),
                  severity: t(
                    `board.severity.${h.overall_rag_because_params?.severity ?? "any"}`,
                    { defaultValue: "" },
                  ),
                  defaultValue: h.overall_rag_because ?? "",
                }),
              )
            : h.overall_rag_because}
        </p>
      )}

      <ToleranceHeadroomLine snapshot={snapshot} />
      <GroupLines snapshot={snapshot} />
    </section>
  );
}

/**
 * P397 / vendor question 5.3 -- consumption and gap to tolerance.
 *
 * A RAG says whether we are in trouble; it cannot say how close the
 * closest service is to being in trouble. Hidden entirely when no IBS
 * carries a declared tolerance, so a bank that has declared none is not
 * shown a reassuring row of zeroes.
 */
function ToleranceHeadroomLine({ snapshot }: { snapshot: BoardSnapshot }) {
  const { t } = useTranslation();
  const th = snapshot.tolerance_headroom;
  if (!th || th.assessed === 0) return null;
  return (
    <p
      data-testid="board-tolerance-headroom"
      data-breached={th.breached}
      data-assessed={th.assessed}
      className="mt-2 text-xs text-slate-600 tabular-nums"
    >
      <span className="font-medium">
        {t("modules.board.headline.toleranceBreached", {
          breached: th.breached,
          assessed: th.assessed,
          mvb: th.mvb_breached,
        })}
      </span>
      {th.worst_overrun_hours !== null && th.worst_overrun_ibs && (
        <span data-testid="board-tolerance-overrun">
          {" · "}
          {t("modules.board.headline.toleranceOverrun", {
            hours: th.worst_overrun_hours,
            ibs: th.worst_overrun_ibs,
          })}
        </span>
      )}
      {th.tightest_headroom_hours !== null && th.tightest_headroom_ibs && (
        <span data-testid="board-tolerance-headroom-tightest">
          {" · "}
          {t("modules.board.headline.toleranceHeadroom", {
            hours: th.tightest_headroom_hours,
            ibs: th.tightest_headroom_ibs,
          })}
        </span>
      )}
      {(th.cif_assessed ?? 0) > 0 && (
        <span data-testid="board-tolerance-cif">
          {" · "}
          {t("modules.board.headline.toleranceCif", {
            breached: th.cif_breached ?? 0,
            assessed: th.cif_assessed,
          })}
        </span>
      )}
      {(th.candidates_assessed ?? 0) > 0 && (
        <span data-testid="board-tolerance-candidates">
          {" · "}
          {(th.candidates_total ?? 0) > (th.candidates_assessed ?? 0)
            ? t("modules.board.headline.toleranceCandidatesOf", {
                breached: th.candidates_breached ?? 0,
                assessed: th.candidates_assessed,
                total: th.candidates_total,
              })
            : t("modules.board.headline.toleranceCandidates", {
                breached: th.candidates_breached ?? 0,
                assessed: th.candidates_assessed,
              })}
        </span>
      )}
    </p>
  );
}

/**
 * P428.3 -- the group grain, the few rows a board governs.
 *
 * Each group reads its WORST member's colour, so a group never looks
 * better than the service inside it that is failing. The ungrouped
 * count is printed rather than omitted: a list that leaves it out
 * reads as complete. Hidden when there are no groups at all.
 */
function GroupLines({ snapshot }: { snapshot: BoardSnapshot }) {
  const { t } = useTranslation();
  const groups = Array.isArray(snapshot.groups) ? snapshot.groups : [];
  if (groups.length === 0) return null;
  const ungrouped = snapshot.ungrouped_admitted ?? 0;
  return (
    <div data-testid="board-groups" className="mt-3 text-xs text-slate-600">
      <p className="font-medium">{t("modules.board.headline.groupsHeading")}</p>
      <ul className="mt-1 space-y-0.5 tabular-nums">
        {groups.map((g) => (
          <li
            key={g.code}
            data-testid="board-group-line"
            data-code={g.code}
            data-resilience={g.resilience ?? ""}
          >
            <span className="font-medium">{g.name}</span>
            {" · "}
            {g.resilience
              ? t(`modules.board.ragWord.${ragWordKey(g.resilience)}`)
              : "--"}
            {" · "}
            {t("modules.board.headline.groupLine", {
              breached: g.breached_now,
              members: g.members,
            })}
            {g.derived_tolerance_hours !== null && g.derived_tolerance_from && (
              <>
                {" · "}
                {t("modules.board.headline.groupTolerance", {
                  hours: g.derived_tolerance_hours,
                  from: g.derived_tolerance_from,
                })}
              </>
            )}
          </li>
        ))}
      </ul>
      {ungrouped > 0 && (
        <p data-testid="board-groups-ungrouped" className="mt-1 text-amber-800">
          {t("modules.board.headline.groupsUngrouped", { count: ungrouped })}
        </p>
      )}
      <Link
        to="/twin/ibs-groups"
        className="mt-1 inline-block text-brand-brassDark hover:underline"
      >
        {t("modules.board.headline.groupsOpen")}
      </Link>
    </div>
  );
}

/**
 * C4 -- a headline number opens what it counts.
 *
 * "9 / 14 IBSes impacted" and "RED" were inert, so the one question a
 * board member asks next -- which nine? -- had no answer on the page
 * that raised it. `to` is optional: a figure with no honest
 * destination stays a figure rather than being given a link that goes
 * somewhere approximate.
 */
/**
 * The overall-RAG chip, shaped like KpiTile on purpose: one anchor
 * covering the whole tile, so the four figures on this strip all
 * behave the same way under a hand.
 */
function RagChip({
  rag,
  weighted,
  to,
  openLabel,
  weightedLabel,
}: {
  rag: Rag;
  weighted: string;
  to: string;
  openLabel: string;
  weightedLabel: string | null;
}) {
  const { t } = useTranslation();
  return (
    <Link
      to={to}
      data-testid="board-overall-rag"
      data-rag={rag}
      data-rag-revenue-weighted={weighted}
      className={`block rounded-lg border px-3 py-3 text-center font-bold uppercase tracking-wide hover:brightness-95 ${
        RAG_TONE[rag]
      }`}
    >
      {/* B1 -- the raw value, which for a fourth state would print
          "not_assessed" on a board pack. The catalogue names it. */}
      <span className="block text-lg">
        {t(`modules.board.ragWord.${ragWordKey(rag)}`)}
      </span>
      <span
        data-testid="board-overall-rag-open"
        className="mt-0.5 block text-[10px] font-medium normal-case underline"
      >
        {openLabel}
      </span>
      {weightedLabel && (
        <span
          data-testid="board-overall-rag-weighted"
          className="mt-0.5 block text-[10px] font-medium normal-case opacity-80"
        >
          {weightedLabel}
        </span>
      )}
    </Link>
  );
}

function KpiTile({
  label,
  value,
  to,
  testid,
}: {
  label: string;
  value: string;
  to?: string;
  testid?: string;
}) {
  const body = (
    <>
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-base font-semibold tabular-nums text-slate-900">
        {value}
      </p>
    </>
  );
  if (!to) {
    return (
      <div
        data-testid={testid}
        className="rounded-lg border border-slate-200 bg-white px-3 py-3 text-center"
      >
        {body}
      </div>
    );
  }
  return (
    <Link
      to={to}
      data-testid={testid}
      className="rounded-lg border border-slate-200 bg-white px-3 py-3 text-center hover:border-brand-brass hover:bg-brand-brass/5"
    >
      {body}
    </Link>
  );
}

/** Round 20 P19 -- what a quoted finding sits on, when that is not an
 *  admitted service. A candidate's breach read as the outcome's own. */
function findingOnKey(o: BoardOutcomeRag): string | null {
  if (o.headline_finding_on_status === "candidate") {
    return o.headline_finding_on_function
      ? "findingOnCandidateFunction"
      : "findingOnCandidateService";
  }
  if (o.headline_finding_on_status === "admitted" && o.headline_finding_on_function) {
    return "findingOnAdmittedFunction";
  }
  return null;
}

function OutcomeRagQuadrant({ outcomes }: { outcomes: BoardOutcomeRag[] }) {
  const { t } = useTranslation();
  return (
    <section className="mb-4">
      <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-2">
        {t("modules.board.outcomeRag.heading")}
      </h2>
      <div
        data-testid="board-outcome-rag"
        className="grid grid-cols-1 md:grid-cols-2 gap-2"
      >
        {outcomes.map((o) => (
          <div
            key={o.outcome}
            data-testid="board-outcome-rag-cell"
            data-outcome={o.outcome}
            data-rag={o.has_members === false ? "no_member" : o.rag}
            className={`rounded-lg border p-3 ${
              RAG_TONE[o.has_members === false ? "not_assessed" : o.rag]
            }`}
          >
            <p className="text-xs font-semibold uppercase tracking-wide opacity-80">
              {said(
                t,
                `enumWords.outcome.${o.outcome}`,
                OUTCOME_LABELS[o.outcome] ?? o.outcome,
              )}
            </p>
            {/* D2 (round 8) -- fifth round of a verdict that is a <p>
                with no anchor or button above it. The board's whole job
                is to send somebody somewhere, and a reader who cannot
                open the verdict has to go and find it.

                The VALUE is the link, not the tile: the tile already
                carries the headline finding's own anchor, and nesting
                one inside another is invalid and makes both
                unpredictable. It opens the cascade filtered to the
                IBSes in this outcome, which is the set the tile
                counted. */}
            {o.has_members === false ? (
              <>
                <p className="text-xl font-bold" data-testid="board-outcome-no-member">
                  {t("modules.board.outcomeRag.noMember")}
                </p>
                <p className="text-xs mt-1">
                  {t("modules.board.outcomeRag.noMemberBody")}
                </p>
              </>
            ) : (
              <>
                <p className="text-xl font-bold">
                  <Link
                    to={`/twin/cascade?kind=ibs_service&outcome=${encodeURIComponent(
                      o.outcome,
                    )}`}
                    data-testid={`board-outcome-rag-open-${o.outcome}`}
                    className="hover:underline"
                  >
                    {/* Round 14 B3 -- the raw value printed NOT_ASSESSED. */}
                    {t(`modules.board.ragWord.${ragWordKey(o.rag)}`)}
                  </Link>
                </p>
                {o.ibs_count > 0 && (
                  <p className="text-xs mt-1" data-testid="board-outcome-rag-ratio">
                    {/* Round 18 P5 -- name the cut when the row holds candidates. */}
                    {o.ibs_admitted != null && o.ibs_admitted < o.ibs_count
                      ? t("modules.board.outcomeRag.impactedRatioCut", {
                          impacted: o.ibs_impacted,
                          total: o.ibs_count,
                          admitted: o.ibs_admitted,
                          assessing: o.ibs_count - o.ibs_admitted,
                        })
                      : t("modules.board.outcomeRag.impactedRatio", {
                          impacted: o.ibs_impacted,
                          total: o.ibs_count,
                        })}
                  </p>
                )}
                {(o.cif_count ?? 0) > 0 && (
                  <p data-testid="board-outcome-rag-cif" className="text-[10px] mt-1">
                    {/* P429.6 -- they colour the quadrant, counted apart. */}
                    {/* Round 20 P19 -- said first when nothing else sits here. */}
                    {o.cif_admitted != null && o.cif_admitted < (o.cif_count ?? 0)
                      ? t(
                          o.ibs_count > 0
                            ? "modules.board.outcomeRag.cifRatioCut"
                            : "modules.board.outcomeRag.cifRatioCutOnly",
                          {
                            impacted: o.cif_impacted ?? 0,
                            total: o.cif_count,
                            admitted: o.cif_admitted,
                            assessing: (o.cif_count ?? 0) - o.cif_admitted,
                          },
                        )
                      : t(
                          o.ibs_count > 0
                            ? "modules.board.outcomeRag.cifRatio"
                            : "modules.board.outcomeRag.cifRatioOnly",
                          {
                            impacted: o.cif_impacted ?? 0,
                            total: o.cif_count,
                          },
                        )}
                  </p>
                )}
                {o.revenue_share !== null && (
                  <p
                    data-testid="board-outcome-rag-share"
                    data-share={o.revenue_share}
                    className="text-[10px] mt-1 opacity-80 tabular-nums"
                  >
                    {t("modules.board.outcomeRag.revenueShare", {
                      pct: Math.round(o.revenue_share * 100),
                    })}
                  </p>
                )}
                {o.headline_finding_title && (
                  <p className="text-xs italic mt-1 truncate">
                    <FindingLink
                      id={o.headline_finding_id}
                      title={o.headline_finding_title}
                    />
                    {o.headline_finding_severity
                      ? ` (${o.headline_finding_severity})`
                      : ""}
                    {findingOnKey(o) && (
                      <span data-testid="board-outcome-finding-on">
                        {" -- "}
                        {t(`modules.board.outcomeRag.${findingOnKey(o)}`)}
                      </span>
                    )}
                  </p>
                )}
              </>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

/** The name, as a way IN. A kind with no detail page stays text
 *  rather than linking somewhere that does not exist. */
/**
 * D4 -- what changed since a date the board chooses.
 *
 * A second read of the SAME composer at an earlier time, so the two
 * figures are built the same way; comparing a live number against one
 * assembled differently is how a "change" appears that never happened.
 */
function BoardComparison({
  value,
  onChange,
  current,
  prior,
  loading,
}: {
  value: string;
  onChange: (v: string) => void;
  current: BoardSnapshot | undefined;
  prior: BoardSnapshot | undefined;
  loading: boolean;
}) {
  const { t } = useTranslation();
  /* Round 11 F4, ninth round -- and the fix that stands one control
     away is why this one is still wrong. Round 10 D3 MEASURED that a
     native date input takes its segment order from the BROWSER and
     ignores the element's `lang`, replaced the as-of control with an
     ISO text field and an Apply button, and left this one on
     `type="date"` with the `lang` that cannot work. So the board still
     showed mm/dd/yyyy to a reader whose every other date reads
     "30 Aug 2026".

     Same shape as the as-of picker, deliberately: ISO order is the
     format the API takes, the format the chain rows print, and the one
     order no locale reads differently -- and it is STATED beside the
     field rather than implied by a browser setting nobody can see. */
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  const parseable = /^\d{4}-\d{2}-\d{2}$/.test(draft.trim());
  // ONLY the figures that genuinely replay at a past time. Every
  // finding-driven number on this board is current-state (the P2.6
  // compromise), so comparing those would report "no change" whatever
  // the date -- a control that looks like it works and cannot.
  // C4 -- the comparison read the sealed resilience chain, and a
  // moment with NO sealed state came back as zero red. On a reseeded
  // estate the board reported "0 to 4": a deterioration that never
  // happened, on the one screen where that reads as news. Nothing
  // sealed is not a number to diff against; say so instead.
  const nothingSealedBefore =
    Boolean(prior) && (prior?.headline.ibs_resilience_sealed ?? 0) === 0;
  const rows =
    current && prior && !nothingSealedBefore
      ? [
          {
            key: "resilienceRed",
            label: t("modules.board.compare.red"),
            now: current.headline.ibs_resilience_red ?? 0,
            was: prior.headline.ibs_resilience_red ?? 0,
          },
          {
            key: "resilienceAmber",
            label: t("modules.board.compare.amber"),
            now: current.headline.ibs_resilience_amber ?? 0,
            was: prior.headline.ibs_resilience_amber ?? 0,
          },
        ]
      : [];
  return (
    <section data-testid="board-comparison" className="mb-3 text-xs">
      <label className="flex flex-wrap items-center gap-2 text-slate-600">
        <span>{t("modules.board.compare.label")}</span>
        <input
          dir="ltr"
          type="text"
          inputMode="numeric"
          data-testid="board-compare-input"
          placeholder={t("modules.board.compare.format")}
          aria-describedby="board-compare-format"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className="w-[8.5rem] rounded border border-slate-300 px-2 py-1 font-mono"
        />
        <span
          id="board-compare-format"
          data-testid="board-compare-format"
          className="font-mono text-[10px] opacity-70"
        >
          {t("modules.board.compare.format")}
        </span>
        <button
          type="button"
          data-testid="board-compare-apply"
          disabled={!parseable}
          onClick={() => onChange(draft.trim())}
          title={
            draft && !parseable ? t("modules.board.compare.formatHint") : undefined
          }
          className="rounded border border-slate-300 px-2 py-1 disabled:opacity-40"
        >
          {t("modules.board.compare.apply")}
        </button>
        {value && (
          <button
            type="button"
            data-testid="board-compare-clear"
            onClick={() => {
              setDraft("");
              onChange("");
            }}
            className="text-slate-500 hover:underline"
          >
            {t("modules.board.compare.clear")}
          </button>
        )}
      </label>
      {loading && <p className="mt-1 text-slate-400">{t("common.loading")}</p>}
      {!loading && nothingSealedBefore && (
        <p data-testid="board-compare-nothing-sealed" className="mt-1 text-amber-800">
          {t("modules.board.compare.nothingSealed", { when: value })}
        </p>
      )}
      {rows.length > 0 && (
        <p className="mt-1 text-slate-400">{t("modules.board.compare.note")}</p>
      )}
      {rows.length > 0 && (
        <ul data-testid="board-comparison-deltas" className="mt-1 flex flex-wrap gap-3">
          {rows.map((r) => {
            const delta = r.now - r.was;
            return (
              <li key={r.key} data-testid={`board-delta-${r.key}`}>
                <span className="text-slate-500">{r.label}:</span>{" "}
                <span className="tabular-nums">{r.was}</span>
                {" \u2192 "}
                <span className="font-medium tabular-nums">{r.now}</span>{" "}
                {/* No arrow on nil change: a "0" dressed as movement
                    reads as movement. */}
                {delta !== 0 && (
                  <span className={delta > 0 ? "text-rag-red" : "text-rag-green"}>
                    ({delta > 0 ? "+" : ""}
                    {delta})
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function HotSpotName({ hotSpot }: { hotSpot: BoardHotSpot }) {
  const path = entityDetailPath(hotSpot.entity_kind, hotSpot.entity_id);
  if (!path) return <>{hotSpot.entity_name}</>;
  return (
    <Link
      data-testid={`board-hot-spot-link-${hotSpot.entity_id}`}
      to={path}
      className="hover:text-brand-brassDark hover:underline"
    >
      {hotSpot.entity_name}
    </Link>
  );
}

function HotSpotsTable({
  hotSpots,
  asOf,
  notAssessed,
}: {
  hotSpots: BoardHotSpot[];
  asOf: string | null;
  notAssessed: boolean;
}) {
  const { t } = useTranslation();
  if (hotSpots.length === 0 && notAssessed) {
    // Round 14 B2 -- "All clear" one panel below NOT YET ASSESSED. No hot
    // spot on an estate nobody has judged is an absence, not a result,
    // and it is not green.
    return (
      <section className="mb-4">
        <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-2">
          {t("modules.board.hotSpots.heading")}
        </h2>
        <p
          data-testid="board-hot-spots-not-assessed"
          className="text-sm italic text-slate-700 bg-slate-50 border border-slate-200 rounded p-3"
        >
          {t("modules.board.hotSpots.emptyNotAssessed")}
        </p>
      </section>
    );
  }
  if (hotSpots.length === 0) {
    return (
      <section className="mb-4">
        <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-2">
          {t("modules.board.hotSpots.heading")}
        </h2>
        <p
          data-testid="board-hot-spots-empty"
          className="text-sm italic text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-3"
        >
          {/* Round 9 B1: "no impacted IBSes right now" under a banner
              dated four days ago is a sentence that is not true of the
              table beneath it. The component already holds asOf. */}
          {t(
            asOf ? "modules.board.hotSpots.emptyAsOf" : "modules.board.hotSpots.empty",
          )}
        </p>
      </section>
    );
  }
  return (
    <section className="mb-4">
      <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-2">
        {t("modules.board.hotSpots.heading")}
      </h2>
      <div className="overflow-x-auto border border-slate-200 rounded">
        <table data-testid="board-hot-spots-table" className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
            <tr>
              <th className="px-3 py-2 text-start">
                {t("modules.board.hotSpots.col.ibs")}
              </th>
              <th className="px-3 py-2 text-start">
                {t("modules.board.hotSpots.col.outcome")}
              </th>
              <th className="px-3 py-2 text-start">
                {t("modules.board.hotSpots.col.severity")}
              </th>
              <th className="px-3 py-2 text-right">
                {t("modules.board.hotSpots.col.open")}
              </th>
              <th className="px-3 py-2 text-right">
                {t("modules.board.hotSpots.col.revenue")}
              </th>
              <th className="px-3 py-2 text-right">
                {t("modules.board.hotSpots.col.journeys")}
              </th>
            </tr>
          </thead>
          <tbody>
            {hotSpots.map((h) => (
              <tr
                key={h.entity_id}
                data-testid="board-hot-spot-row"
                data-entity-id={h.entity_id}
                className="border-t border-slate-100"
              >
                {/* D4 -- the row named the IBS and went nowhere, so
                    "why is this one red?" had no next click. */}
                <td className="px-3 py-2 font-medium text-slate-900">
                  <HotSpotName hotSpot={h} />
                </td>
                <td className="px-3 py-2 text-xs text-slate-600">
                  {h.outcome_category
                    ? (OUTCOME_LABELS[h.outcome_category] ?? h.outcome_category)
                    : "--"}
                </td>
                <td className="px-3 py-2 text-xs uppercase font-semibold">
                  {h.severity}
                  {/* C4 -- the as-of banner tells the reader that
                      anything raised after the chosen time "is marked
                      below", and the board drew no marker: it promised
                      an honesty it did not deliver, which is worse
                      than the limitation it was explaining. */}
                  {isAfterAsOf(h.headline_finding_raised_at ?? null, asOf) && (
                    <span
                      data-testid="board-hot-spot-after-as-of"
                      className="ms-1 rounded bg-sky-100 px-1 py-0.5 text-[9px] font-medium normal-case text-sky-900"
                    >
                      {t("asOfBanner.later")}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {h.open_finding_count}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {sarOrDash(h.annual_revenue_at_risk_via_products)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{h.journey_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function MvbBlock({ mvb }: { mvb: BoardSnapshot["mvb"] }) {
  const { t } = useTranslation();
  if (mvb.declaration_id === null) {
    return (
      <section
        data-testid="board-mvb-empty"
        className="rounded-lg border border-slate-200 bg-white p-3"
      >
        <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1">
          {t("screens.boardSnapshotView.mvb")}
        </h3>
        <p className="text-sm italic text-slate-500">{t("modules.board.mvb.empty")}</p>
      </section>
    );
  }
  return (
    <section
      data-testid="board-mvb"
      className="rounded-lg border border-slate-200 bg-white p-3 space-y-1"
    >
      <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
        {t("screens.boardSnapshotView.mvb")}
      </h3>
      <p className="font-medium text-slate-900">{mvb.declaration_name}</p>
      <p className="text-xs text-slate-600">
        {t("modules.board.mvb.readiness", {
          value: mvb.readiness_state ?? "--",
        })}
      </p>
      <p className="text-xs text-slate-600">
        {mvb.ibs_count_scoped_admitted != null
          ? t("modules.board.mvb.scopedAdmitted", {
              count: mvb.ibs_count_scoped,
              admitted: mvb.ibs_count_scoped_admitted,
            })
          : t("modules.board.mvb.scoped", { count: mvb.ibs_count_scoped })}
      </p>
      <p
        className={`text-xs ${
          mvb.review_due_in_days !== null && mvb.review_due_in_days < 0
            ? "font-medium text-rag-red"
            : "text-slate-600"
        }`}
        data-testid="board-mvb-review-due"
      >
        {mvb.review_due_in_days === null
          ? t("modules.board.mvb.reviewDueUnknown")
          : mvb.review_due_in_days < 0
            ? t("modules.board.mvb.reviewOverdue", {
                count: -mvb.review_due_in_days,
              })
            : mvb.review_due_in_days === 0
              ? t("modules.board.mvb.reviewDueToday")
              : t("modules.board.mvb.reviewDue", {
                  count: mvb.review_due_in_days,
                })}
      </p>
      {mvb.review_before_effective && (
        <p
          className="text-xs text-amber-900"
          data-testid="board-mvb-review-before-effective"
        >
          {t("modules.board.mvb.reviewBeforeEffective")}
        </p>
      )}
      <p className="text-xs">
        {mvb.dual_signoff_complete ? (
          <span className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-900 px-2 py-0.5 text-xs font-medium">
            {t("modules.board.mvb.signoffComplete")}
          </span>
        ) : (
          <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-900 px-2 py-0.5 text-xs font-medium">
            {t("modules.board.mvb.signoffIncomplete")}
          </span>
        )}
      </p>
    </section>
  );
}

function FrameworkBlock({ fa }: { fa: BoardSnapshot["framework_assessments"] }) {
  const { t } = useTranslation();
  if (fa.assessments_total === 0) {
    return (
      <section
        data-testid="board-framework-empty"
        className="rounded-lg border border-slate-200 bg-white p-3"
      >
        <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1">
          {t("modules.board.framework.heading")}
        </h3>
        <p className="text-sm italic text-slate-500">
          {t("modules.board.framework.empty")}
        </p>
      </section>
    );
  }
  return (
    <section
      data-testid="board-framework"
      className="rounded-lg border border-slate-200 bg-white p-3 space-y-2"
    >
      <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
        {t("modules.board.framework.heading")}
      </h3>
      <p className="text-xs text-slate-600">
        {t("modules.board.framework.cyclesOpen", { count: fa.cycles_open })}
      </p>
      <div>
        <p className="text-xs text-slate-600 mb-1 tabular-nums">
          {t("modules.board.framework.approvedRatio", {
            approved: fa.assessments_approved,
            total: fa.assessments_total,
            pct: fa.approved_pct,
          })}
        </p>
        <div
          data-testid="board-framework-progress"
          className="h-2 w-full rounded bg-slate-200 overflow-hidden"
        >
          <div
            data-testid="board-framework-progress-fill"
            className="h-full bg-emerald-600 transition-all"
            style={{ width: `${fa.approved_pct}%` }}
          />
        </div>
      </div>
      <p className="text-xs text-slate-600 tabular-nums">
        {t("modules.board.framework.inReview", {
          count: fa.assessments_in_review,
        })}{" "}
        ·{" "}
        {t("modules.board.framework.draft", {
          count: fa.assessments_draft,
        })}
      </p>
    </section>
  );
}
